import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '@/lib/api';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

export default function PricingHistoryPage() {
  const { variantId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState(null);
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [filters, setFilters] = useState({
    entityType: 'ALL',
    fromDate: '',
    toDate: '',
    changedBy: ''
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);

  const loadVariant = async () => {
    if (!variantId) return;
    try {
      const response = await adminApi.getVariantPricingModule(variantId);
      setVariant(response.data?.variant || null);
    } catch (error) {
      toast.error('Failed to load variant details');
    }
  };

  const loadHistory = async () => {
    if (!variantId) return;
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        entity_type: appliedFilters.entityType === 'ALL' ? undefined : appliedFilters.entityType,
        from_date: appliedFilters.fromDate || undefined,
        to_date: appliedFilters.toDate || undefined,
        changed_by: appliedFilters.changedBy || undefined
      };
      const response = await adminApi.getVariantPriceHistory(variantId, params);
      const data = response.data || {};
      setHistory(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVariant();
  }, [variantId]);

  useEffect(() => {
    loadHistory();
  }, [variantId, page, pageSize, appliedFilters]);

  const handleApplyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  return (
    <div className="space-y-6" data-testid="pricing-history-page">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate(`/products/${variantId}/pricing`)}
          className="text-[#5C4033]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pricing
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Pricing History</h1>
        <p className="text-sm text-[#A8A8A8]">
          {variant?.master_name || 'Product'} · {variant?.pack_size_label || ''} · {variant?.sku || ''}
        </p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-[#5C4033]">Entity Type</label>
              <Select
                value={filters.entityType}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, entityType: value }))}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="ALL">All</SelectItem>
                  <SelectItem value="COSTING">Costing</SelectItem>
                  <SelectItem value="PRICING">Pricing</SelectItem>
                  <SelectItem value="RULES">Rules</SelectItem>
                  <SelectItem value="DISCOUNT">Discounts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[#5C4033]">From Date</label>
              <Input
                type="date"
                value={filters.fromDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[#5C4033]">To Date</label>
              <Input
                type="date"
                value={filters.toDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[#5C4033]">Changed By</label>
              <Input
                value={filters.changedBy}
                onChange={(e) => setFilters((prev) => ({ ...prev, changedBy: e.target.value }))}
                placeholder="Name or ID"
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-[#5C4033]">Page Size</label>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => setPageSize(Number(value))}
              >
                <SelectTrigger className="border-[#E5E5E5] w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleApplyFilters} className="bg-[#C97A5E] hover:bg-[#B06850]">
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : history.length === 0 ? (
            <div className="p-6 text-sm text-[#A8A8A8]">No history found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Changed By</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {history.map((entry) => (
                    <tr key={entry.id}>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">
                        {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.entity_type}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.changed_by?.name || '-'}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-sm text-[#A8A8A8]">Page {page} of {totalPages} · {total} records</p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            className="border-[#E5E5E5]"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            className="border-[#E5E5E5]"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
